import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WebsiteAnalyticsService } from '@/services/website-analytics.service';
import { WebsiteAnalyticsSummary, WebsiteVisitor } from '@/models/website-analytics.model';
import { subDays, format, startOfDay, endOfDay } from 'date-fns';

export const WebsiteAnalytics: React.FC = () => {
  const { profile } = useAuth();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<WebsiteAnalyticsSummary | null>(null);
  const [recentVisitors, setRecentVisitors] = useState<WebsiteVisitor[]>([]);
  const [selectedVisitor, setSelectedVisitor] = useState<WebsiteVisitor | null>(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date;

      switch (timeRange) {
        case '7d':
          startDate = startOfDay(subDays(now, 7));
          break;
        case '30d':
          startDate = startOfDay(subDays(now, 30));
          break;
        case '90d':
          startDate = startOfDay(subDays(now, 90));
          break;
        default:
          startDate = new Date(0);
      }

      const endDate = endOfDay(now);

      console.log('[WebsiteAnalytics] Loading analytics:', {
        timeRange,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      const [summary, visitors] = await Promise.all([
        WebsiteAnalyticsService.getAnalyticsSummary(startDate, endDate),
        WebsiteAnalyticsService.getRecentVisitors(20),
      ]);

      console.log('[WebsiteAnalytics] Data loaded:', {
        summary: summary ? {
          totalVisitors: summary.totalVisitors,
          totalSessions: summary.totalSessions,
          totalConversions: summary.totalConversions,
        } : null,
        recentVisitorsCount: visitors.length,
      });

      setAnalytics(summary);
      setRecentVisitors(visitors);
    } catch (error: any) {
      console.error('[WebsiteAnalytics] Error loading website analytics:', error);
      // Show user-friendly error message
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        alert('Firestore index is being created. Please wait a few minutes and refresh. If the issue persists, contact support.');
      }
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    if (profile) {
      loadAnalytics();
    }
  }, [profile, loadAnalytics]);

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const getSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      organic: 'bg-green-100 text-green-800',
      paid: 'bg-blue-100 text-blue-800',
      direct: 'bg-gray-100 text-gray-800',
      social: 'bg-purple-100 text-purple-800',
      email: 'bg-yellow-100 text-yellow-800',
      referral: 'bg-indigo-100 text-indigo-800',
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading website analytics...</p>
      </div>
    );
  }

  // Show data even if summary calculation fails - at least show visitors list
  const hasData = analytics || recentVisitors.length > 0;

  if (!hasData && !loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-600 mb-2">No analytics data available</p>
        <p className="text-sm text-gray-500">
          Visit your website to start tracking visitors. Data will appear here once visitors are tracked.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Website Visitor Analytics</h2>
          <p className="mt-1 text-sm text-gray-500">
            Track visitors, conversions, and optimize your marketing funnel
          </p>
        </div>
        <div className="flex space-x-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics - Only show if analytics data is available */}
      {analytics && (
        <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Visitors</div>
          <div className="text-2xl font-bold text-gray-900">{analytics.totalVisitors.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {analytics.newVisitors} new, {analytics.returningVisitors} returning
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Conversion Rate</div>
          <div className="text-2xl font-bold text-green-600">{analytics.conversionRate.toFixed(2)}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {analytics.totalConversions} conversions
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Avg Session Duration</div>
          <div className="text-2xl font-bold text-blue-600">
            {formatDuration(analytics.averageSessionDuration)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {analytics.pagesPerSession.toFixed(1)} pages/session
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Bounce Rate</div>
          <div className="text-2xl font-bold text-orange-600">{analytics.bounceRate.toFixed(1)}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {analytics.totalSessions} total sessions
          </div>
        </div>
      </div>

      {/* Lead Qualification */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Potential Employers</div>
          <div className="text-2xl font-bold text-purple-600">{analytics.potentialEmployers}</div>
          <div className="text-xs text-gray-500 mt-1">High-value leads</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Potential Providers</div>
          <div className="text-2xl font-bold text-indigo-600">{analytics.potentialProviders}</div>
          <div className="text-xs text-gray-500 mt-1">Provider signups</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">High Interest Visitors</div>
          <div className="text-2xl font-bold text-green-600">{analytics.highInterestVisitors}</div>
          <div className="text-xs text-gray-500 mt-1">Engaged prospects</div>
        </div>
      </div>
      </>)}

      {/* Recent Visitors */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Visitors {recentVisitors.length > 0 && `(${recentVisitors.length})`}
        </h3>
        {recentVisitors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-2">No recent visitors found</p>
            <p className="text-sm text-gray-500">
              Visitor data will appear here once visitors are tracked on your website.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Make sure the tracking script is properly installed on all website pages.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interest
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentVisitors.map((visitor) => (
                <tr
                  key={visitor.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedVisitor(visitor)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(visitor.sessionStartTime, 'MMM d, h:mm a')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(visitor.source)}`}>
                      {visitor.source}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {visitor.deviceType} / {visitor.browser}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {visitor.city ? `${visitor.city}, ` : ''}{visitor.region || ''} {visitor.country || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {visitor.pagesViewed.length}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDuration(visitor.timeOnSite)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {visitor.interestLevel && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        visitor.interestLevel === 'high' ? 'bg-green-100 text-green-800' :
                        visitor.interestLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {visitor.interestLevel}
                      </span>
                    )}
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visitor Detail Modal */}
      {selectedVisitor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Visitor Details</h3>
              <button
                onClick={() => setSelectedVisitor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">Session Info</h4>
                <p className="text-sm text-gray-600">Started: {format(selectedVisitor.sessionStartTime, 'PPpp')}</p>
                <p className="text-sm text-gray-600">Duration: {formatDuration(selectedVisitor.timeOnSite)}</p>
                <p className="text-sm text-gray-600">Pages: {selectedVisitor.pagesViewed.join(', ')}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Traffic Source</h4>
                <p className="text-sm text-gray-600">Source: {selectedVisitor.source}</p>
                {selectedVisitor.utmCampaign && (
                  <p className="text-sm text-gray-600">Campaign: {selectedVisitor.utmCampaign}</p>
                )}
                {selectedVisitor.gclid && (
                  <p className="text-sm text-gray-600">Google Click ID: {selectedVisitor.gclid}</p>
                )}
              </div>
              {selectedVisitor.interestSignals && selectedVisitor.interestSignals.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900">Interest Signals</h4>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {selectedVisitor.interestSignals.map((signal, idx) => (
                      <li key={idx}>{signal}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

